import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Banknote,
  Copy,
  Download,
  Facebook,
  HeartHandshake,
  Landmark,
  Loader2,
  QrCode,
  ReceiptText,
} from "lucide-react";
import { api, receiptUrl } from "@/lib/api";
import { DONATION_PRESETS, TRUST } from "@/content";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (document.getElementById("razorpay-js")) return resolve(true);
    const s = document.createElement("script");
    s.id = "razorpay-js";
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const TABS = [
  { id: "online", label: "ઓનલાઈન", icon: HeartHandshake },
  { id: "upi", label: "UPI", icon: QrCode },
  { id: "bank", label: "બેંક", icon: Landmark },
];

export default function Donate() {
  const [tab, setTab] = useState("online");
  const [amount, setAmount] = useState(1100);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [onlineEnabled, setOnlineEnabled] = useState(false);
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [receipt, setReceipt] = useState(null);
  const [waOptIn, setWaOptIn] = useState(false);

  const finalAmount = custom ? parseInt(custom, 10) || 0 : amount;
  const preset = useMemo(
    () => DONATION_PRESETS.find((p) => p.amount === finalAmount),
    [finalAmount]
  );

  const refresh = async () => {
    try {
      const [s, r] = await Promise.all([api.getStats(), api.getRecent()]);
      setStats(s);
      setRecent(r);
    } catch {
      /* stats are non-blocking */
    }
  };

  useEffect(() => {
    api
      .getConfig()
      .then((c) => setOnlineEnabled(c.razorpay_enabled))
      .catch(() => setOnlineEnabled(false));
    refresh();
  }, []);

  const copyText = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} કોપી થયું`);
  };

  const validate = () => {
    if (name.trim().length < 2) {
      toast.error("કૃપા કરી તમારું નામ લખો");
      return false;
    }
    if (finalAmount < 10) {
      toast.error("લઘુત્તમ રકમ ₹10 છે");
      return false;
    }
    return true;
  };

  const payOnline = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      if (!onlineEnabled) throw new Error("offline");
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("offline");
      const order = await api.createOrder({
        name: name.trim(),
        phone: phone.trim() || null,
        amount: finalAmount,
        whatsapp_opt_in: waOptIn,
      });
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: TRUST.name,
        description: "દાન",
        order_id: order.order_id,
        prefill: { name: name.trim(), contact: phone.trim() },
        theme: { color: "#E67E22" },
        handler: async (resp) => {
          try {
            await api.verifyDonation({
              donation_id: order.donation_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setReceipt({ id: order.donation_id, amount: finalAmount, name: name.trim() });
            refresh();
          } catch {
            toast.error("ચુકવણી ચકાસણીમાં ભૂલ — અમારો સંપર્ક કરો");
          }
        },
      });
      rzp.open();
    } catch {
      toast.error("ઓનલાઈન ચુકવણી હાલ ઉપલબ્ધ નથી — UPI અથવા બેંક વિકલ્પ વાપરો");
      setTab("upi");
    } finally {
      setLoading(false);
    }
  };

  const pledgeManual = async (method) => {
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await api.manualDonation({
        name: name.trim(),
        phone: phone.trim() || null,
        amount: finalAmount,
        method,
        whatsapp_opt_in: waOptIn,
      });
      setReceipt({ id: res.id, amount: finalAmount, name: name.trim() });
      refresh();
    } catch {
      toast.error("કંઈક ખોટું થયું — ફરી પ્રયત્ન કરો");
    } finally {
      setLoading(false);
    }
  };

  const resetDonation = () => {
    setReceipt(null);
    setName("");
    setPhone("");
    setCustom("");
  };

  return (
    <section id="donate" data-testid="donate-section" className="relative py-20 lg:py-24 bg-leaf overflow-hidden">
      <div className="noise-overlay" />
      <div className="absolute -top-24 -left-24 w-[380px] h-[380px] rounded-full bg-white/10 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <p className="font-head font-bold text-sm tracking-[0.2em] text-saffron-soft mb-4">
            દાન કરો
          </p>
          <h2 className="font-display text-cream text-4xl sm:text-5xl leading-snug">
            તમારું દાન,
            <br />
            કોઈની <span className="text-saffron">ચાવીરૂપ મદદ</span>
          </h2>
          <p className="mt-6 text-cream/85 text-base md:text-lg leading-relaxed max-w-md">
            દરેક રૂપિયો સીધો દિવ્યાંગ બંધુની સાધન સહાય, દવા, અનાજ કે શિક્ષણમાં
            ફરવાય છે. રોજના માત્ર ₹1 એટલે કે માસિક ₹30 થી પણ તમે અમારી સાથે
            જોડાઈ શકો છો.
          </p>
          <div className="mt-5 flex flex-wrap gap-2 max-w-md">
            <span
              data-testid="donation-80g-badge"
              className="rounded-full bg-saffron/90 px-4 py-1.5 font-head font-bold text-xs text-cream"
            >
              80G કરમુક્તિ લાભયોગ્ય
            </span>
            <span className="rounded-full bg-white/10 border border-white/15 px-4 py-1.5 font-head font-bold text-xs text-cream/85">
              CSR નં: CSR00058798
            </span>
            <span className="rounded-full bg-white/10 border border-white/15 px-4 py-1.5 font-head font-bold text-xs text-cream/85">
              રજિ. નં: E/7950
            </span>
          </div>

          {stats && (
            <div
              data-testid="donation-live-stats"
              className="mt-8 grid grid-cols-2 gap-4 max-w-md"
            >
              <div className="rounded-2xl bg-white/10 backdrop-blur-sm border border-white/15 p-5">
                <p className="font-display text-2xl text-cream">
                  ₹{(stats.total_paid + stats.total_pledged).toLocaleString("en-IN")}
                </p>
                <p className="font-head text-sm text-cream/75 mt-1">આ માસે એકત્ર</p>
              </div>
              <div className="rounded-2xl bg-white/10 backdrop-blur-sm border border-white/15 p-5">
                <p className="font-display text-2xl text-cream">
                  {stats.donor_count + stats.member_count}
                </p>
                <p className="font-head text-sm text-cream/75 mt-1">દાતા અને સભ્યો</p>
              </div>
            </div>
          )}

          {recent.length > 0 && (
            <div data-testid="recent-donors-wall" className="mt-8 max-w-md">
              <p className="font-head font-bold text-cream/90 text-sm mb-3">
                તાજેતરના સહાયકારો — આભાર
              </p>
              <div className="flex flex-wrap gap-2">
                {recent.slice(0, 8).map((d, i) => (
                  <span
                    key={i}
                    className="rounded-full bg-white/10 border border-white/15 px-3.5 py-1.5 font-head text-xs text-cream/90"
                  >
                    {d.name} · ₹{d.amount.toLocaleString("en-IN")}
                  </span>
                ))}
              </div>
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="lg:col-span-7"
        >
          <div className="bg-cream rounded-3xl p-6 sm:p-9 shadow-[0_30px_80px_rgb(0,0,0,0.25)]">
            {receipt ? (
              <div data-testid="donation-success-card" className="text-center py-6">
                <div className="w-20 h-20 rounded-full bg-leaf-soft border border-leaf/30 flex items-center justify-center mx-auto mb-5">
                  <ReceiptText className="w-10 h-10 text-leaf" />
                </div>
                <h3 className="font-display text-3xl text-ink mb-3">
                  આભાર, {receipt.name}!
                </h3>
                <p className="font-head text-ink/70 max-w-sm mx-auto mb-2">
                  તમારું ₹{receipt.amount.toLocaleString("en-IN")} નું દાન નોંધાઈ
                  ગયું છે. દરેક રૂપિયો દિવ્યાંગ બંધુઓની સેવામાં વપરાશે.
                </p>
                <p className="font-head text-sm text-leaf-dark font-semibold mb-7">
                  80G કરમુક્તિ માટે તમારી રસીદ ડાઉનલોડ કરી લો
                </p>
                <a
                  data-testid="donation-receipt-download"
                  href={receiptUrl(receipt.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2.5 rounded-full bg-leaf px-8 py-4 font-head font-bold text-cream text-lg shadow-[0_14px_40px_rgb(30,132,73,0.35)] hover:bg-leaf-dark hover:-translate-y-0.5 transition-all duration-200"
                >
                  <Download className="w-5 h-5" />
                  રસીદ ડાઉનલોડ કરો (PDF)
                </a>
                <a
                  data-testid="donation-share-facebook"
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                    TRUST.siteUrl
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 rounded-full border-2 border-[#1877F2]/30 px-6 py-2.5 font-head font-bold text-sm text-[#1877F2] hover:bg-[#1877F2] hover:text-white transition-colors duration-200"
                >
                  <Facebook className="w-4 h-4" />
                  Facebook પર શેર કરો
                </a>
                <button
                  data-testid="donation-new-button"
                  onClick={resetDonation}
                  className="block mx-auto mt-5 font-head font-semibold text-sm text-ink/50 hover:text-saffron transition-colors"
                >
                  નવું દાન કરો →
                </button>
              </div>
            ) : (
              <>
                <div
                  data-testid="donation-tabs"
                  className="flex gap-2 rounded-full bg-stone-100 p-1.5 mb-7 w-fit"
                >
                  {TABS.map((t) => (
                    <button
                      key={t.id}
                      data-testid={`donation-tab-${t.id}`}
                      onClick={() => setTab(t.id)}
                      className={`flex items-center gap-2 rounded-full px-5 py-2.5 font-head font-bold text-sm transition-all duration-200 ${
                        tab === t.id
                          ? "bg-terracotta text-cream shadow-md"
                          : "text-ink/60 hover:text-ink"
                      }`}
                    >
                      <t.icon className="w-4 h-4" />
                      {t.label}
                    </button>
                  ))}
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-5">
                  <div>
                    <label className="font-head font-semibold text-sm text-ink/70 block mb-1.5">
                      તમારું નામ *
                    </label>
                    <input
                      data-testid="donation-name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="પૂરું નામ"
                      className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 font-body text-ink outline-none focus:border-saffron focus:ring-2 focus:ring-saffron/20 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="font-head font-semibold text-sm text-ink/70 block mb-1.5">
                      મોબાઈલ નંબર (SMS રસીદ માટે)
                    </label>
                    <input
                      data-testid="donation-phone-input"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/[^0-9+]/g, ""))}
                      placeholder="98XXX XXXXX"
                      className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 font-body text-ink outline-none focus:border-saffron focus:ring-2 focus:ring-saffron/20 transition-colors"
                    />
                  </div>
                </div>

                <p className="font-head font-semibold text-sm text-ink/70 mb-2.5">
                  રકમ પસંદ કરો
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  {DONATION_PRESETS.map((p) => (
                    <button
                      key={p.amount}
                      data-testid={`donation-amount-${p.amount}`}
                      onClick={() => {
                        setAmount(p.amount);
                        setCustom("");
                      }}
                      className={`rounded-xl border-2 px-3 py-3 font-head font-bold transition-all duration-200 ${
                        finalAmount === p.amount && !custom
                          ? "border-saffron bg-saffron-soft text-saffron-dark"
                          : "border-stone-200 bg-white text-ink/70 hover:border-saffron/50"
                      }`}
                    >
                      ₹{p.amount.toLocaleString("en-IN")}
                    </button>
                  ))}
                </div>
                <input
                  data-testid="donation-custom-amount-input"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value.replace(/[^0-9]/g, ""))}
                  placeholder="અથવા પોતાની રકમ લખો (₹)"
                  className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 font-body text-ink outline-none focus:border-saffron focus:ring-2 focus:ring-saffron/20 transition-colors mb-3"
                />
                <p data-testid="donation-impact-note" className="font-head text-sm text-leaf-dark font-semibold min-h-[20px] mb-4">
                  {preset ? `₹${preset.amount.toLocaleString("en-IN")} = ${preset.impact}` : ""}
                </p>

                <label className="flex items-start gap-3 mb-6 cursor-pointer select-none">
                  <input
                    data-testid="donation-whatsapp-optin"
                    type="checkbox"
                    checked={waOptIn}
                    onChange={(e) => setWaOptIn(e.target.checked)}
                    className="mt-1 w-4 h-4 shrink-0 accent-[#1E8449]"
                  />
                  <span className="font-head text-sm text-ink/70">
                    હું ટ્રસ્ટ તરફથી WhatsApp પર દાન રસીદ અને સંદેશા મેળવવા સંમત છું
                  </span>
                </label>

                {tab === "online" && (
                  <div data-testid="donation-online-panel">
                    <button
                      data-testid="donation-pay-online-button"
                      onClick={payOnline}
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2.5 rounded-full bg-saffron px-7 py-4 font-head font-bold text-cream text-lg shadow-[0_14px_40px_rgb(230,126,34,0.4)] hover:bg-saffron-dark hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60 disabled:translate-y-0"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <HeartHandshake className="w-5 h-5" />
                      )}
                      ₹{finalAmount.toLocaleString("en-IN")} દાન કરો
                    </button>
                    {!onlineEnabled && (
                      <p className="mt-3 text-center font-head text-xs text-ink/50">
                        ઓનલાઈન ચુકવણી ટૂંક સમયમાં — અત્યારે UPI/બેંક વિકલ્પ વાપરો
                      </p>
                    )}
                  </div>
                )}

                {tab === "upi" && (
                  <div data-testid="donation-upi-panel" className="text-center">
                    <div className="mx-auto w-52 rounded-2xl border border-stone-200 bg-white p-3 shadow-sm mb-4">
                      <img
                        src="/upi-qr.jpeg"
                        alt="દાન માટે UPI QR કોડ"
                        data-testid="donation-upi-qr-image"
                        className="w-full rounded-xl"
                      />
                    </div>
                    <p className="font-head text-sm text-ink/60 mb-5">
                      કોઈપણ UPI એપ (GPay / PhonePe / Paytm) થી સ્કેન કરી દાન કરો
                    </p>
                    <button
                      data-testid="donation-upi-pledge-button"
                      onClick={() => pledgeManual("upi")}
                      disabled={loading}
                      className="mt-5 w-full flex items-center justify-center gap-2 rounded-full bg-terracotta px-7 py-3.5 font-head font-bold text-cream hover:bg-terracotta/90 transition-colors duration-200 disabled:opacity-60"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Banknote className="w-5 h-5" />}
                      મેં UPI થી દાન કર્યું છે
                    </button>
                  </div>
                )}

                {tab === "bank" && (
                  <div data-testid="donation-bank-panel">
                    <dl className="rounded-2xl border border-stone-200 bg-white divide-y divide-stone-100 mb-5">
                      {[
                        ["બેંક", TRUST.bank.bankName],
                        ["ખાતાનું નામ", TRUST.bank.name],
                        ["ખાતા નંબર", TRUST.bank.account],
                        ["IFSC", TRUST.bank.ifsc],
                        ["શાખા", TRUST.bank.branch],
                      ].map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between px-5 py-3.5 gap-3">
                          <dt className="font-head text-sm text-ink/55">{k}</dt>
                          <dd className="flex items-center gap-2 font-head font-bold text-sm text-ink text-right">
                            {v}
                            {(k === "ખાતા નંબર" || k === "IFSC") && (
                              <button
                                data-testid={`donation-bank-copy-${k === "IFSC" ? "ifsc" : "account"}`}
                                onClick={() => copyText(v, k)}
                                className="text-saffron hover:text-saffron-dark transition-colors"
                                aria-label={`${k} કોપી કરો`}
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                            )}
                          </dd>
                        </div>
                      ))}
                    </dl>
                    <button
                      data-testid="donation-bank-pledge-button"
                      onClick={() => pledgeManual("bank")}
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2 rounded-full bg-terracotta px-7 py-3.5 font-head font-bold text-cream hover:bg-terracotta/90 transition-colors duration-200 disabled:opacity-60"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Landmark className="w-5 h-5" />}
                      મેં બેંકમાંથી દાન કર્યું છે
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
